import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from 'components/ui-components/Button';
import Card from 'components/ui-components/Card';
import { useForm } from 'react-hook-form';
import { HookFormProvider } from 'components/cef_form/hook-form/providers/HookFormProvider';
import responsive from 'utils/responsive';
import toRem from 'utils/toRem';
import { useHTTP } from 'hooks/useRequest';
import { secondaryTextColor, grey3 } from 'constants/variables';
import {
  minPasswordLength,
  maxPasswordLength,
  NO_LEADING_TRAILING_SPACE,
} from 'constants/constants';
import ErrorBox from './errorBox';
import PasswordInput from './passwordInput';

const PasswordResetForm = ({ endpoint, customerId, customerKey }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const {
    control,
    watch,
    trigger,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const onSubmit = () => {
    clearErrors();
    setFormSubmitted(true);
  };

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const passwordResetConfig = {
    method: 'PUT',
    url: formSubmitted ? endpoint : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      update_password: {
        password,
        password_confirmation: confirmPassword,
        id: customerId,
        key: customerKey,
      },
    }),
  };

  const { data, error, loading } = useHTTP(passwordResetConfig || null);

  useEffect(() => {
    if (!data && !error) return;

    setFormSubmitted(false);

    const { url, error: responseError } = data || error;

    if (url) window.location.href = url;

    if (responseError) {
      const {
        status,
        data: { errors: errorMessages },
      } = responseError;

      if (status === 404)
        window.location.href = responseError.request.responseURL;

      if (errorMessages)
        errorMessages.forEach(errorMessage => {
          setError('serverError', {
            type: status,
            message: errorMessage,
          });
        });
    }
  }, [data, error]);

  const passwordLengthErrorMessage = `Your password must be ${minPasswordLength} - ${maxPasswordLength} characters long`;

  return (
    <HookFormProvider
      control={control}
      trigger={trigger}
      watch={watch}
      setValue={setValue}
      setError={setError}
      clearErrors={clearErrors}
      errors={errors}>
      <FormWrapper className="margin-center width-100">
        <div className="ta-c mb-32">
          <h1 className="ff-rc fw-700 fs-30 l-fs-36 lh-1-2 l-lh-1 tc-main mb-12 mt-52">
            Reset Password
          </h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormCard variant="large" className="mb-32">
            <div>
              <PasswordInput
                label="New Password"
                id="password"
                rules={{
                  required: 'Please enter a password',
                  minLength: {
                    value: minPasswordLength,
                    message: passwordLengthErrorMessage,
                  },
                  maxLength: {
                    value: maxPasswordLength,
                    message: passwordLengthErrorMessage,
                  },
                  validate: {
                    leadingTrailingSpaces: value =>
                      NO_LEADING_TRAILING_SPACE.test(value) ||
                      'Your password cannot have spaces at the start or end',
                  },
                }}
              />

              <PasswordRequirements className="mt-6">
                <p className="fw-700 mb-8">Password Requirements</p>
                <ul className="mb-0">
                  <li className="mb-6">
                    Must be {minPasswordLength} - {maxPasswordLength} characters
                    long
                  </li>
                  <li>Cannot have spaces at the start or end</li>
                </ul>
              </PasswordRequirements>
            </div>

            <PasswordInput
              label="Confirm New Password"
              id="confirmPassword"
              rules={{
                required: 'Please confirm your new password',
                validate: value =>
                  value === password || 'The passwords entered do not match',
              }}
            />

            {errors?.serverError && (
              <ErrorBox text={errors.serverError.message} />
            )}

            <SubmitButton
              type="submit"
              className="cef-btn cef-btn--green cef-btn--feature width-100 mt-16">
              {loading ? (
                <span className="cef-btn__loader bs-bb" />
              ) : (
                'Update Password'
              )}
            </SubmitButton>
          </FormCard>
        </form>
      </FormWrapper>
    </HookFormProvider>
  );
};

const FormWrapper = styled.div`
  ${responsive('desktopSmall')} {
    max-width: 650px;
  }
`;

const FormCard = styled(Card)`
  display: grid;
  gap: ${toRem(24)};
`;

const PasswordRequirements = styled.div`
  background-color: ${grey3};
  padding: ${toRem(10)} ${toRem(16)};

  li {
    list-style-type: square;
    color: ${secondaryTextColor};
  }
`;

const SubmitButton = styled(Button)`
  ${responsive('desktopSmall')} {
    max-width: 250px;
  }
`;

export default PasswordResetForm;
